import { useCallback, useEffect, useMemo, useState } from "react";
import { retroApi } from "../api/retroApi";
import { RETRO_MOCK, RETRO_TABS } from "../mock/retroMockData";
import { average } from "../services/retroDerivations";

const ZONE_LEVELS = [
  "Needs Structured Support",
  "Execution Driven",
  "Independent Solver",
  "System Builder",
  "Strategic Owner",
];

const PROMPT_DEFS = [
  { key: "went_well", label: "What went well", maxChars: 600 },
  { key: "do_better", label: "What can be done better", maxChars: 600 },
  { key: "improve", label: "What I am going to improve", maxChars: 600 },
  { key: "repeat", label: "Where I'm going repeat", maxChars: 600 },
];

const VISIBILITY_OPTIONS = [
  { value: "reviewer", label: "Reviewer" },
  { value: "skip_level", label: "Skip-level" },
  { value: "team", label: "Team" },
  { value: "private", label: "Private" },
];

function fmtShortDate(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
}

function statusTitle(value) {
  const s = String(value || "").trim().toLowerCase();
  if (!s) return "Draft";
  if (s === "not_started") return "Not started";
  return s
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function mapReflectionsFromAnswers(periods = [], answersByPeriod = {}) {
  const latest = [...periods].sort((a, b) => new Date(b.period_start) - new Date(a.period_start))[0];
  const rows = latest ? (answersByPeriod[String(latest.id)] || []) : [];
  const byKey = Object.fromEntries(rows.map((r) => [r.prompt_key, r.body || ""]));

  return PROMPT_DEFS.map((p) => ({
    key: p.key,
    title: p.label,
    body: byKey[p.key] || "No input yet.",
  }));
}

function mapDashboard({ summary, cycle, goals, reflections }) {
  const radarRows = summary?.radar_snapshot || [];
  const axes = radarRows.map((r) => r.competency);
  const target = radarRows.map((r) => Number(r.target_score || 0));
  const selfValues = radarRows.map((r) => Number(r.self_score || 0));
  const periodDate = radarRows[0]?.period_start;
  const radarLabel = periodDate ? `${fmtShortDate(periodDate)} · ${cycle?.name || "Current cycle"}` : (cycle?.name || "Current cycle");

  const radar = {
    axes: axes.length ? axes : RETRO_MOCK.dashboard.radar.axes,
    target: target.length ? target : RETRO_MOCK.dashboard.radar.target,
    seriesByPeriod: {
      week: [{ id: `cycle-${cycle?.id || "now"}`, label: radarLabel, values: selfValues.length ? selfValues : RETRO_MOCK.dashboard.radar.seriesByPeriod.week[0].values }],
      month: [{ id: `cycle-${cycle?.id || "now"}`, label: radarLabel, values: selfValues.length ? selfValues : RETRO_MOCK.dashboard.radar.seriesByPeriod.month[0].values }],
      quarter: [{ id: `cycle-${cycle?.id || "now"}`, label: radarLabel, values: selfValues.length ? selfValues : RETRO_MOCK.dashboard.radar.seriesByPeriod.quarter[0].values }],
      year: [{ id: `cycle-${cycle?.id || "now"}`, label: radarLabel, values: selfValues.length ? selfValues : RETRO_MOCK.dashboard.radar.seriesByPeriod.year[0].values }],
    },
  };

  const diffs = radarRows.map((r) => {
    const selfScore = Number(r.self_score || 0);
    const targetScore = Number(r.target_score || 0);
    return {
      title: r.competency,
      diff: selfScore - targetScore,
    };
  });

  const strengths = diffs
    .filter((d) => d.diff >= 0)
    .sort((a, b) => b.diff - a.diff)
    .slice(0, 4)
    .map((d) => ({ title: d.title, meta: `+${d.diff.toFixed(1)} vs target` }));

  const growthAreas = diffs
    .filter((d) => d.diff < 0)
    .sort((a, b) => a.diff - b.diff)
    .slice(0, 4)
    .map((d) => ({ title: d.title, meta: `${d.diff.toFixed(1)} vs target` }));

  const mappedGoals = goals.map((g, idx) => ({
    code: g.code || `G-${String(idx + 1).padStart(2, "0")}`,
    title: g.title,
    progress: Number(g.progress_pct || 0),
    status: g.status,
  }));

  const playbook = mappedGoals.slice(0, 4).map((g, idx) => ({
    id: `p-${idx + 1}`,
    seq: String(idx + 1).padStart(2, "0"),
    title: g.title,
    tag: g.status.replace(/_/g, " "),
    done: g.status === "done",
    eta: `${Math.round(g.progress)}%`,
  }));

  const competencies = radarRows.slice(0, 3).map((r) => ({ key: r.competency, value: Number(r.self_score || 0) }));
  const avgScore = average(selfValues);

  return {
    zone: {
      current: summary?.current_zone?.current_zone || RETRO_MOCK.dashboard.zone.current,
      target: summary?.current_zone?.target_zone || RETRO_MOCK.dashboard.zone.target,
      levels: ZONE_LEVELS,
    },
    radar,
    trend: [{
      id: `trend-${cycle?.id || "now"}`,
      label: cycle?.name || "Now",
      sub: periodDate ? fmtShortDate(periodDate) : "",
      score: Number.isFinite(avgScore) ? avgScore : 0,
    }],
    strengths: strengths.length ? strengths : RETRO_MOCK.dashboard.strengths,
    growthAreas: growthAreas.length ? growthAreas : RETRO_MOCK.dashboard.growthAreas,
    playbook,
    goals: mappedGoals,
    competencies: competencies.length ? competencies : RETRO_MOCK.dashboard.competencies,
    reflections,
  };
}

function mapTeamMembers(teamRetros = [], teamSummaryByResource = {}) {
  return teamRetros.map((tr) => {
    const summary = teamSummaryByResource[tr.resource_id] || {};
    const radar = summary.radar_snapshot || [];
    const selfVals = radar.map((r) => Number(r.self_score || 0));
    const targetVals = radar.map((r) => Number(r.target_score || 0));
    const score = average(selfVals);
    const targetAvg = average(targetVals);
    const goalPct = Number(summary.goal_attainment?.avg_progress_pct || 0);

    return {
      id: tr.resource_id,
      name: tr.resource_name || String(tr.resource_id).slice(0, 8),
      role: "Team member",
      current: tr.current_zone || "—",
      target: tr.target_zone || "—",
      score: Number.isFinite(score) ? score : 0,
      delta: Number.isFinite(score) && Number.isFinite(targetAvg) ? score - targetAvg : 0,
      goalPct,
      tenure: "—",
      status: statusTitle(tr.status),
      retroId: tr.retro_id,
      cycleId: tr.cycle_id,
    };
  });
}

function mapTeamRollup(members = []) {
  if (!members.length) {
    return [
      { key: "Team avg", value: "0.0/10", meta: "No reports" },
      { key: "Submissions", value: "0/0", meta: "0 in · 0 pending" },
      { key: "Goal attainment", value: "0%", meta: "Median across team" },
      { key: "Flags raised", value: "0 open", meta: "Career-growth signals" },
      { key: "Feedback pending", value: "0 / 0", meta: "Pending reviews" },
    ];
  }

  const teamAvg = average(members.map((m) => m.score));
  const submitted = members.filter((m) => ["Submitted", "Approved"].includes(m.status)).length;
  const pending = members.length - submitted;
  const goalAvg = average(members.map((m) => Number(m.goalPct || 0)));
  const flags = members.filter((m) => Number(m.goalPct || 0) < 60).length;
  const feedbackPending = members.filter((m) => m.status !== "Approved").length;

  return [
    { key: "Team avg", value: `${teamAvg.toFixed(1)}/10`, meta: `Across ${members.length} reports` },
    { key: "Submissions", value: `${submitted}/${members.length}`, meta: `${submitted} in · ${pending} pending` },
    { key: "Goal attainment", value: `${Math.round(goalAvg)}%`, meta: "Median across team" },
    { key: "Flags raised", value: `${flags} open`, meta: "Career-growth signals" },
    { key: "Feedback pending", value: `${feedbackPending} / ${members.length}`, meta: "Pending reviews" },
  ];
}

function buildSubmitWeeks(periods = [], answersByPeriod = {}) {
  return [...periods]
    .sort((a, b) => new Date(b.period_start) - new Date(a.period_start))
    .map((p) => {
      const answers = answersByPeriod[String(p.id)] || [];
      const answersByKey = {};
      answers.forEach((a) => {
        answersByKey[a.prompt_key] = a.body || "";
      });
      return {
        id: String(p.id),
        label: `${p.frequency} · ${fmtShortDate(p.period_start)}`,
        submitted: p.status !== "Draft",
        answers: answersByKey,
      };
    });
}

function buildViewModel({
  cycle,
  myRetro,
  summary,
  goals,
  myReflectionPeriods,
  answersByPeriod,
  teamRetros,
  teamSummaryByResource,
}) {
  const reflections = mapReflectionsFromAnswers(myReflectionPeriods, answersByPeriod);
  const dashboard = mapDashboard({ summary, cycle, goals, reflections });

  const members = mapTeamMembers(teamRetros, teamSummaryByResource);
  const rollup = mapTeamRollup(members);
  const submitWeeks = buildSubmitWeeks(myReflectionPeriods, answersByPeriod);

  return {
    cycle: {
      title: "Performance Retro",
      cycleLabel: cycle?.name ? `${cycle.name} cycle` : "Current cycle",
      status: statusTitle(cycle?.status || myRetro?.status || "draft"),
      id: cycle?.id || null,
    },
    dashboard,
    team: {
      rollup,
      members,
      memberDetails: {},
    },
    learnings: RETRO_MOCK.learnings,
    submitReflection: {
      reviewerName: "Reviewer",
      weeks: submitWeeks,
      promptDefs: PROMPT_DEFS,
      visibilityOptions: VISIBILITY_OPTIONS,
    },
  };
}

const EMPTY_VIEW_MODEL = {
  cycle: {
    title: "Performance Retro",
    cycleLabel: "Current cycle",
    status: "Draft",
    id: null,
  },
  dashboard: {
    zone: {
      current: ZONE_LEVELS[0],
      target: ZONE_LEVELS[0],
      levels: ZONE_LEVELS,
    },
    radar: {
      axes: RETRO_MOCK.dashboard.radar.axes,
      target: RETRO_MOCK.dashboard.radar.target,
      seriesByPeriod: {
        week: [],
        month: [],
        quarter: [],
        year: [],
      },
    },
    trend: [],
    strengths: [],
    growthAreas: [],
    playbook: [],
    goals: [],
    competencies: [],
    reflections: [],
  },
  team: {
    rollup: [],
    members: [],
    memberDetails: {},
  },
  learnings: RETRO_MOCK.learnings,
  submitReflection: {
    reviewerName: "Reviewer",
    weeks: [],
    promptDefs: PROMPT_DEFS,
    visibilityOptions: VISIBILITY_OPTIONS,
  },
};

async function tryOrDefault(factory, fallback) {
  try {
    return await factory();
  } catch {
    return fallback;
  }
}

export function useRetroData() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewModel, setViewModel] = useState(EMPTY_VIEW_MODEL);

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [cycles, myRetros] = await Promise.all([
        retroApi.listCycles(),
        retroApi.listMyRetros(),
      ]);

      const cycle = cycles?.[0] || null;
      const myRetro = (myRetros || []).find((r) => r.cycle_id === cycle?.id) || myRetros?.[0] || null;

      const [summary, goals, myReflectionPeriods, teamRetros] = await Promise.all([
        cycle ? tryOrDefault(() => retroApi.getDashboardSummary({ cycleId: cycle.id }), {}) : Promise.resolve({}),
        myRetro ? tryOrDefault(() => retroApi.listGoals(myRetro.retro_id), []) : Promise.resolve([]),
        cycle ? tryOrDefault(() => retroApi.listReflectionPeriods({ cycle_id: cycle.id }), []) : Promise.resolve([]),
        tryOrDefault(() => retroApi.listTeamRetros(cycle ? { cycle_id: cycle.id } : {}), []),
      ]);

      const periodIds = (myReflectionPeriods || []).map((p) => p.id);
      const answersByPeriodEntries = await Promise.all(
        periodIds.map(async (periodId) => {
          const rows = await tryOrDefault(() => retroApi.listReflectionAnswers(periodId), []);
          return [String(periodId), rows];
        })
      );
      const answersByPeriod = Object.fromEntries(answersByPeriodEntries);

      const teamSummaryEntries = await Promise.all(
        (teamRetros || []).slice(0, 50).map(async (tr) => {
          if (!tr?.cycle_id || !tr?.resource_id) return [tr?.resource_id || "", {}];
          const summaryRow = await tryOrDefault(
            () => retroApi.getDashboardSummary({ cycleId: tr.cycle_id, resourceId: tr.resource_id }),
            {}
          );
          return [tr.resource_id, summaryRow];
        })
      );
      const teamSummaryByResource = Object.fromEntries(teamSummaryEntries);

      const model = buildViewModel({
        cycle,
        myRetro,
        summary,
        goals,
        myReflectionPeriods,
        answersByPeriod,
        teamRetros,
        teamSummaryByResource,
      });

      setViewModel(model);
    } catch (err) {
      setError(err?.message || "Failed to load retro data");
      setViewModel(EMPTY_VIEW_MODEL);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return useMemo(
    () => ({ loading, error, viewModel, reload, tabs: RETRO_TABS }),
    [loading, error, viewModel, reload]
  );
}
